import { useState, useEffect, useCallback } from 'react'
import type { GameItem } from '../../i18n'
import { shuffle } from '../utils'

export type Difficulty = 'easy' | 'medium' | 'hard'
export type GameMode = 'images' | 'definitions' | 'anagram'

// Number of answer choices per question (0 = free-text input)
export const DIFFICULTY_CHOICES: Record<Difficulty, number> = {
  easy:   4,
  medium: 6,
  hard:   0,
}

export const QUESTIONS_PER_GAME = 8

function buildOptions(correct: GameItem, all: GameItem[], count: number): GameItem[] {
  const distractors = shuffle(all.filter(i => i.id !== correct.id)).slice(0, count - 1)
  return shuffle([correct, ...distractors])
}

function scrambleWord(word: string): string {
  const letters = word.split('')
  let scrambled: string
  do {
    scrambled = shuffle([...letters]).join('')
  } while (scrambled === word && word.length > 1)
  return scrambled
}

export interface QuizState {
  questions:       GameItem[]
  currentIdx:      number
  options:         GameItem[]   // choices for current question (easy/medium)
  eliminated:      Set<number>  // wrong option ids for current question
  answered:        number       // count of correctly answered questions
  moves:           number       // total attempts (including wrong ones)
  correctFlashId:  number | null
  startTime:       number | null
  endTime:         number | null
  gameComplete:    boolean
  // hard mode
  inputValue:      string
  hintLetterCount: boolean
  hintFirstLetter: boolean
  inputStatus:     'idle' | 'correct' | 'wrong'
  // anagram mode
  scrambledLetters: string
}

function initState(difficulty: Difficulty, items: GameItem[], mode: GameMode): QuizState {
  const n = Math.min(QUESTIONS_PER_GAME, items.length)
  const questions = shuffle([...items]).slice(0, n)
  const choices = DIFFICULTY_CHOICES[difficulty]
  const options =
    difficulty !== 'hard' && questions.length > 0
      ? buildOptions(questions[0], items, choices)
      : []
  const firstWord = questions.length > 0 ? questions[0].word : ''
  return {
    questions,
    currentIdx:      0,
    options,
    eliminated:      new Set(),
    answered:        0,
    moves:           0,
    correctFlashId:  null,
    startTime:       null,
    endTime:         null,
    gameComplete:    false,
    inputValue:      '',
    hintLetterCount: false,
    hintFirstLetter: false,
    inputStatus:     'idle',
    scrambledLetters: mode === 'anagram' && firstWord ? scrambleWord(firstWord) : '',
  }
}

export function useWordMatch(difficulty: Difficulty, items: GameItem[], mode: GameMode = 'images') {
  const [state, setState] = useState<QuizState>(() => initState(difficulty, items, mode))

  const resetGame = useCallback(() => {
    setState(initState(difficulty, items, mode))
  }, [difficulty, items, mode])

  // Advance to next question after the correct-answer flash
  useEffect(() => {
    if (state.correctFlashId === null) return
    const timer = setTimeout(() => {
      setState((prev) => {
        const nextIdx = prev.currentIdx + 1
        if (nextIdx >= prev.questions.length) {
          return { ...prev, correctFlashId: null, gameComplete: true, endTime: Date.now() }
        }
        const choices = DIFFICULTY_CHOICES[difficulty]
        const nextQ   = prev.questions[nextIdx]
        const options = difficulty !== 'hard' ? buildOptions(nextQ, items, choices) : []
        const scrambled = mode === 'anagram' ? scrambleWord(nextQ.word) : ''
        return {
          ...prev,
          currentIdx:      nextIdx,
          options,
          eliminated:      new Set(),
          correctFlashId:  null,
          inputValue:      '',
          hintLetterCount: false,
          hintFirstLetter: false,
          inputStatus:     'idle',
          scrambledLetters: scrambled,
        }
      })
    }, 700)
    return () => clearTimeout(timer)
  }, [state.correctFlashId, difficulty, items, mode])

  // Clear 'wrong' input status after shake animation
  useEffect(() => {
    if (state.inputStatus !== 'wrong') return
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, inputStatus: 'idle' }))
    }, 600)
    return () => clearTimeout(timer)
  }, [state.inputStatus])

  const selectOption = useCallback((optionId: number) => {
    setState((prev) => {
      if (prev.correctFlashId !== null || prev.gameComplete) return prev
      if (prev.eliminated.has(optionId)) return prev
      const current   = prev.questions[prev.currentIdx]
      const startTime = prev.startTime ?? Date.now()
      const moves     = prev.moves + 1
      if (optionId === current.id) {
        return { ...prev, moves, startTime, answered: prev.answered + 1, correctFlashId: optionId }
      }
      const eliminated = new Set(prev.eliminated)
      eliminated.add(optionId)
      return { ...prev, moves, startTime, eliminated }
    })
  }, [])

  const setInputValue = useCallback((val: string) => {
    setState((prev) => ({ ...prev, inputValue: val, inputStatus: 'idle' }))
  }, [])

  const submitInput = useCallback(() => {
    setState((prev) => {
      if (prev.correctFlashId !== null || prev.gameComplete) return prev
      const current   = prev.questions[prev.currentIdx]
      const normalize = (s: string) =>
        s.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const answer    = normalize(prev.inputValue)
      const correct   = normalize(current.word)
      const startTime = prev.startTime ?? Date.now()
      const moves     = prev.moves + 1
      if (answer === correct) {
        return {
          ...prev, moves, startTime,
          answered:       prev.answered + 1,
          inputStatus:    'correct',
          correctFlashId: current.id,
        }
      }
      return { ...prev, moves, startTime, inputStatus: 'wrong' }
    })
  }, [])

  const toggleHint = useCallback((type: 'letterCount' | 'firstLetter') => {
    setState((prev) => {
      if (type === 'letterCount') return { ...prev, hintLetterCount: !prev.hintLetterCount }
      return { ...prev, hintFirstLetter: !prev.hintFirstLetter }
    })
  }, [])

  return { state, resetGame, selectOption, setInputValue, submitInput, toggleHint }
}
